/**
 * `noteCommands` (W1-10a core-commands-structure; DESIGN.md §5.3, §5.4; RPE NOTE-01..15, EXEC-01..07).
 *
 * Rules every note command keeps:
 *  - notes never go on a UI-mover line (Phira draws neither the line nor its notes: "Notes on this line would be
 *    invisible");
 *  - non-hold notes end where they start; holds end after they start. An imported zero-length hold (end === t) is
 *    kept as it is until its times are edited (setNoteTimes, moveNotes with dt != 0, quantizeNotes); then it becomes
 *    a hold of one snap step (the `snapDiv` argument, default `ZERO_HOLD_STEP_DIV`, or the quantize grid). Moving
 *    only in X or to another line, or
 *    patching other fields, keeps it as it is;
 *  - field values stay in their model ranges (finite numbers, integer alpha 0..65535, appear time >= 0, judge width
 *    > 0, u8 tints); a custom hitsound is an existing sound file not named 'tap.mp3' / 'drag.mp3' / 'flick.mp3'
 *    (Phira would play its built-in sound instead);
 *  - deleted ids in a selection are skipped (selections can be stale after undo).
 * New and moved notes are selected and the transaction focuses their beat ("jump to change").
 */

import { Expr } from '../expr/index';
import type { ExprScope } from '../expr/types';
import { RPE_VISIBLE_ALWAYS } from '../model/defaults';
import type { Hitsound, LineId, Note, NoteId, NoteKind, NoteTemplate } from '../model/types';
import type { RecordPatch } from '../ops/types';
import { Beats, quantizeBeat } from '../time/beat';
import type { Beat } from '../time/types';
import { hitsoundAssetProblem } from './assets';
import {
  existingNotes,
  isFiniteNumber,
  isU8Triple,
  lineLabel,
  lineOf,
  noNegZero,
  noteOf,
  type Patch,
  type Tx,
} from './structureKit';
import type { NoteCommands, NoteDraft, NoteField } from './types';

/** Snap grid (1/div beat) a zero-length imported hold gets as its length when its times are edited. */
export const ZERO_HOLD_STEP_DIV = 4;
/** Half the RPE play field width in RPE-x px (X grid of EXEC-07: -675 .. 675). */
export const HALF_WIDTH_PX = 675;
/** Largest snap divisor the UI offers. */
export const MAX_SNAP_DIV = 1920;

/** Built-in hit sounds (checked at run time too: arguments may come from pasted or raw data). */
const BUILTIN_SOUNDS: ReadonlySet<string> = new Set(['tap', 'drag', 'flick']);

type NoteFields = Partial<Omit<Note, 'id' | 'line' | 'kind' | 't' | 'end' | 'src' | 'gen'>>;

// ---------------------------------------------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------------------------------------------

/** Fails when the line cannot hold notes (missing or UI mover). */
function noteLine(tx: Tx, id: LineId): void {
  const line = lineOf(tx, id);
  if (line.attachUI !== null) {
    tx.fail({ key: 'cmd.notes.uiMover', params: { line: lineLabel(tx, line), element: line.attachUI } });
  }
}

function badField(tx: Tx, field: string): never {
  return tx.fail({ key: 'cmd.notes.badField', params: { field } });
}

/** Validates note field values (only the fields present). */
function checkFields(tx: Tx, f: NoteFields): void {
  for (const field of ['x', 'speed', 'yOffset', 'size'] as const) {
    const v = f[field];
    if (v !== undefined && !isFiniteNumber(v)) badField(tx, field);
  }
  if (f.alpha !== undefined && !(Number.isInteger(f.alpha) && f.alpha >= 0 && f.alpha <= 65535)) badField(tx, 'alpha');
  if (f.visibleTime !== undefined && f.visibleTime !== null && !(isFiniteNumber(f.visibleTime) && f.visibleTime >= 0))
    badField(tx, 'visibleTime');
  if (f.judgeArea !== undefined && f.judgeArea !== null && !(isFiniteNumber(f.judgeArea) && f.judgeArea > 0))
    badField(tx, 'judgeArea');
  if (f.tint !== undefined && f.tint !== null && !isU8Triple(f.tint)) badField(tx, 'tint');
  if (f.tintHitEffects !== undefined && f.tintHitEffects !== null && !isU8Triple(f.tintHitEffects))
    badField(tx, 'tintHitEffects');
  if (f.above !== undefined && typeof f.above !== 'boolean') badField(tx, 'above');
  if (f.fake !== undefined && typeof f.fake !== 'boolean') badField(tx, 'fake');
  if (f.hitsound !== undefined) checkHitsound(tx, f.hitsound);
}

function checkHitsound(tx: Tx, h: Hitsound | null): void {
  if (h === null) return;
  if (h.kind === 'builtin') {
    if (!BUILTIN_SOUNDS.has(h.sound)) badField(tx, 'hitsound');
    return;
  }
  const a = tx.doc.assets.get(h.asset);
  if (a === undefined) return tx.fail({ key: 'cmd.missing.asset', params: { id: h.asset } });
  // A sound file whose name Phira does not take for one of its built-in sounds (whatever the asset's role).
  const problem = hitsoundAssetProblem(a);
  if (problem !== null) tx.fail(problem);
}

function checkBeat(tx: Tx, b: Beat): void {
  if (!Beats.isNormal(b)) tx.fail({ key: 'cmd.badBeat' });
}

// ---------------------------------------------------------------------------------------------------------------
// Times
// ---------------------------------------------------------------------------------------------------------------

/** One snap step (1/div beat) of the zero-length-hold rule. */
function zeroHoldEnd(t: Beat, div = ZERO_HOLD_STEP_DIV): Beat {
  return Beats.step(t, div, 1);
}

/**
 * Times of a note after an edit: non-holds end at t; holds keep end > t (a zero-length hold being edited becomes a
 * one-step hold). Returns the end and whether the zero-length rule applied.
 */
function editedEnd(kind: NoteKind, t: Beat, end: Beat, div: number): { end: Beat; grown: boolean } {
  if (kind !== 'hold') return { end: t, grown: false };
  if (Beats.lt(t, end)) return { end, grown: false };
  return { end: zeroHoldEnd(t, div), grown: true };
}

/** The snap divisor of the zero-length-hold rule: the caller's `snapDiv` (1..MAX_SNAP_DIV), else the default. */
function zeroHoldDiv(tx: Tx, snapDiv: number | undefined): number {
  if (snapDiv === undefined) return ZERO_HOLD_STEP_DIV;
  if (!(Number.isInteger(snapDiv) && snapDiv >= 1 && snapDiv <= MAX_SNAP_DIV)) tx.fail({ key: 'cmd.notes.snapDiv' });
  return snapDiv;
}

/** Notes how many zero-length holds became holds of 1/div beat. */
function noteGrownNote(tx: Tx, count: number, div = ZERO_HOLD_STEP_DIV): void {
  if (count > 0) tx.note({ key: 'cmd.notes.zeroHoldsGrown', params: { count, div } });
}

/** Earliest beat of a set of notes (for focus). */
function firstBeat(notes: readonly Note[]): Beat | undefined {
  let best: Beat | undefined;
  for (const n of notes) if (best === undefined || Beats.lt(n.t, best)) best = n.t;
  return best;
}

function focusOn(tx: Tx, notes: readonly Note[], line?: LineId): void {
  const beat = firstBeat(notes);
  if (beat === undefined) return;
  tx.setFocus(line === undefined ? { beat } : { beat, line });
}

// ---------------------------------------------------------------------------------------------------------------
// Creation
// ---------------------------------------------------------------------------------------------------------------

function insertNote(tx: Tx, draft: NoteDraft, template: NoteTemplate): NoteId {
  noteLine(tx, draft.line);
  checkBeat(tx, draft.t);
  if (Beats.cmp(draft.t, Beats.ZERO) < 0) tx.fail({ key: 'cmd.notes.beforeStart' });
  let end = draft.t;
  if (draft.kind === 'hold') {
    if (draft.end === undefined || !Beats.isNormal(draft.end) || !Beats.lt(draft.t, draft.end)) {
      tx.fail({ key: 'cmd.notes.holdEnd' });
    }
    end = draft.end;
  }
  const o = draft.overrides ?? {};
  const fields: NoteFields = {
    x: noNegZero(draft.x),
    above: o.above ?? template.above,
    fake: o.fake ?? template.fake,
    speed: o.speed ?? template.speed,
    yOffset: o.yOffset ?? template.yOffset,
    size: o.size ?? template.size,
    alpha: o.alpha ?? template.alpha,
    visibleTime: o.visibleTime !== undefined ? o.visibleTime : template.visibleTime,
    hitsound: o.hitsound ?? null,
    tint: o.tint ?? null,
    tintHitEffects: o.tintHitEffects ?? null,
    judgeArea: o.judgeArea ?? null,
  };
  checkFields(tx, fields);
  const id = tx.newId('notes');
  const rec: Note = {
    id,
    line: draft.line,
    kind: draft.kind,
    t: draft.t,
    end,
    x: fields.x as number,
    above: fields.above as boolean,
    fake: fields.fake as boolean,
    speed: fields.speed as number,
    yOffset: fields.yOffset as number,
    size: fields.size as number,
    alpha: fields.alpha as number,
    visibleTime: fields.visibleTime ?? null,
    hitsound: fields.hitsound ?? null,
    tint: fields.tint ?? null,
    tintHitEffects: fields.tintHitEffects ?? null,
    judgeArea: fields.judgeArea ?? null,
    gen: null,
  };
  tx.insert('notes', rec);
  return id;
}

const addNotes: NoteCommands['addNotes'] = (tx: Tx, { drafts, template }) => {
  const ids = drafts.map((d) => insertNote(tx, d, template));
  const notes = ids.map((id) => tx.doc.notes.get(id) as Note);
  const first = drafts[0];
  tx.setSelection({ notes: ids, keys: [], segments: [], ...(first !== undefined ? { activeLine: first.line } : {}) });
  focusOn(tx, notes, first?.line);
  return ids;
};

const addNote: NoteCommands['addNote'] = (tx: Tx, { draft, template }) => {
  const [id] = addNotes(tx, { drafts: [draft], template });
  return id as NoteId;
};

// ---------------------------------------------------------------------------------------------------------------
// Deletion and patches
// ---------------------------------------------------------------------------------------------------------------

const deleteNotes: NoteCommands['deleteNotes'] = (tx: Tx, { ids }) => {
  const notes = existingNotes(tx, ids);
  for (const n of notes) tx.remove('notes', n.id);
  if (notes.length > 0) tx.setSelection({ notes: [] });
};

const patchNotes: NoteCommands['patchNotes'] = (tx: Tx, { ids, patch }) => {
  const p = patch as RecordPatch<Note>;
  // Required fields cannot be removed; `undefined` members are ignored.
  const clean: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(p)) if (v !== undefined) clean[k] = v;
  if ('x' in clean) clean.x = noNegZero(clean.x as number);
  checkFields(tx, clean);
  for (const n of existingNotes(tx, ids)) tx.update('notes', n.id, clean);
};

/** Current value of a note field as an expression variable. */
function fieldValue(n: Note, field: NoteField): number {
  switch (field) {
    case 'x':
    case 'speed':
    case 'size':
    case 'yOffset':
    case 'alpha':
      return n[field];
    case 'visibleTime':
      return n.visibleTime ?? RPE_VISIBLE_ALWAYS;
    case 'judgeArea':
      return n.judgeArea ?? 1;
  }
}

/** Expression result to a stored field value (alpha integral 0..255, appear time >= 0, judge width > 0). */
function storedValue(field: NoteField, v: number): number | null {
  switch (field) {
    case 'alpha':
      return Math.min(255, Math.max(0, Math.round(v)));
    case 'visibleTime':
      return v >= RPE_VISIBLE_ALWAYS ? null : Math.max(0, v);
    case 'judgeArea':
      return Math.max(0.01, v);
    default:
      return noNegZero(v);
  }
}

/** Notes in time order (t, x, id): the order expressions see as i = 0..n-1. */
function timeOrder(notes: Note[]): Note[] {
  return notes.sort((a, b) => Beats.cmp(a.t, b.t) || a.x - b.x || a.id - b.id);
}

const exprNotes: NoteCommands['exprNotes'] = (tx: Tx, { ids, field, prog, seed }) => {
  const notes = timeOrder(existingNotes(tx, ids));
  const n = notes.length;
  const rng = Expr.rng(seed);
  let changed = 0;
  notes.forEach((note, i) => {
    const scope: ExprScope = {
      v: fieldValue(note, field),
      i,
      n,
      u: n > 1 ? i / (n - 1) : 0,
      t: Beats.toF64(note.t),
      s: tx.tempo.secAt(note.t),
      x: note.x,
      speed: note.speed,
      size: note.size,
    };
    const result = Expr.apply(prog, scope, rng, tx.tempo);
    if (result === null || !Number.isFinite(result)) {
      tx.fail({ key: 'cmd.notes.exprFailed', params: { index: i + 1, count: n } });
    }
    const value = storedValue(field, result);
    const before = field === 'visibleTime' ? note.visibleTime : field === 'judgeArea' ? note.judgeArea : note[field];
    if (value === before) return;
    tx.update('notes', note.id, { [field]: value });
    changed++;
  });
  return changed;
};

// ---------------------------------------------------------------------------------------------------------------
// Time and line changes
// ---------------------------------------------------------------------------------------------------------------

const moveNotes: NoteCommands['moveNotes'] = (tx: Tx, { ids, dt, dx, toLine, snapDiv }) => {
  checkBeat(tx, dt);
  const div = zeroHoldDiv(tx, snapDiv);
  if (!isFiniteNumber(dx)) badField(tx, 'x');
  if (toLine !== undefined) noteLine(tx, toLine);
  const notes = existingNotes(tx, ids);
  // Only a move in time edits the times: moving in X or to another line keeps a zero-length hold as it is.
  const timesEdited = !Beats.eq(dt, Beats.ZERO);
  let grown = 0;
  const moved: Note[] = [];
  for (const n of notes) {
    const t = Beats.add(n.t, dt, tx.rounding);
    if (Beats.cmp(t, Beats.ZERO) < 0) tx.fail({ key: 'cmd.notes.beforeStart' });
    let end = Beats.add(n.end, dt, tx.rounding);
    if (n.kind !== 'hold') {
      end = t;
    } else if (timesEdited) {
      const r = editedEnd('hold', t, end, div);
      if (r.grown) grown++;
      end = r.end;
    }
    const patch: Patch<Note> = {};
    if (!Beats.eq(t, n.t)) patch.t = t;
    if (!Beats.eq(end, n.end)) patch.end = end;
    if (dx !== 0) patch.x = noNegZero(n.x + dx);
    if (toLine !== undefined && toLine !== n.line) patch.line = toLine;
    if (Object.keys(patch).length > 0) tx.update('notes', n.id, patch);
    moved.push(tx.doc.notes.get(n.id) as Note);
  }
  if (moved.length > 0 && !Beats.eq(dt, Beats.ZERO)) focusOn(tx, moved, toLine);
  if (toLine !== undefined && moved.length > 0) tx.setSelection({ activeLine: toLine, notes: moved.map((n) => n.id) });
  noteGrownNote(tx, grown, div);
};

const setNoteTimes: NoteCommands['setNoteTimes'] = (tx: Tx, { id, t, end, snapDiv }) => {
  const n = noteOf(tx, id);
  const div = zeroHoldDiv(tx, snapDiv);
  const nt = t ?? n.t;
  checkBeat(tx, nt);
  if (Beats.cmp(nt, Beats.ZERO) < 0) tx.fail({ key: 'cmd.notes.beforeStart' });
  let ne: Beat;
  let grown = false;
  if (n.kind !== 'hold') {
    ne = nt;
  } else if (end !== undefined) {
    checkBeat(tx, end);
    if (!Beats.lt(nt, end)) tx.fail({ key: 'cmd.notes.holdEnd' });
    ne = end;
  } else if (Beats.eq(n.t, n.end)) {
    ne = zeroHoldEnd(nt, div);
    grown = true;
  } else {
    ne = n.end;
    if (!Beats.lt(nt, ne)) tx.fail({ key: 'cmd.notes.holdEnd' });
  }
  const patch: Patch<Note> = {};
  if (!Beats.eq(nt, n.t)) patch.t = nt;
  if (!Beats.eq(ne, n.end)) patch.end = ne;
  if (Object.keys(patch).length > 0) tx.update('notes', id, patch);
  if (grown) noteGrownNote(tx, 1, div);
  tx.setFocus({ beat: nt, line: n.line });
};

const convertNotes: NoteCommands['convertNotes'] = (tx: Tx, { ids, kind, holdLen }) => {
  if (kind === 'hold' && !(Beats.isNormal(holdLen) && Beats.cmp(holdLen, Beats.ZERO) > 0)) {
    tx.fail({ key: 'cmd.notes.holdLength' });
  }
  for (const n of existingNotes(tx, ids)) {
    if (n.kind === kind) continue;
    const end = kind === 'hold' ? Beats.add(n.t, holdLen, tx.rounding) : n.t;
    tx.update('notes', n.id, { kind, end });
  }
};

// ---------------------------------------------------------------------------------------------------------------
// Value actions (EXEC-01..07)
// ---------------------------------------------------------------------------------------------------------------

const mirrorNotesX: NoteCommands['mirrorNotesX'] = (tx: Tx, { ids, around }) => {
  const notes = existingNotes(tx, ids);
  if (notes.length === 0) return;
  let c = 0;
  if (around === 'center') {
    let lo = Infinity;
    let hi = -Infinity;
    for (const n of notes) {
      lo = Math.min(lo, n.x);
      hi = Math.max(hi, n.x);
    }
    c = (lo + hi) / 2;
  }
  for (const n of notes) {
    const x = noNegZero(2 * c - n.x);
    if (x !== n.x) tx.update('notes', n.id, { x });
  }
};

const setSide: NoteCommands['setSide'] = (tx: Tx, { ids, above }) => {
  for (const n of existingNotes(tx, ids)) {
    const next = above === 'toggle' ? !n.above : above;
    if (next !== n.above) tx.update('notes', n.id, { above: next });
  }
};

const setFake: NoteCommands['setFake'] = (tx: Tx, { ids, fake }) => {
  for (const n of existingNotes(tx, ids)) {
    const next = fake === 'toggle' ? !n.fake : fake;
    if (next !== n.fake) tx.update('notes', n.id, { fake: next });
  }
};

/** Nearest of `count` evenly spaced X grid lines across the field: x_i = -675 + i * 1350 / (count - 1). */
export function snapXToGrid(x: number, count: number): number {
  const step = (2 * HALF_WIDTH_PX) / (count - 1);
  const i = Math.min(count - 1, Math.max(0, Math.round((x + HALF_WIDTH_PX) / step)));
  return noNegZero(-HALF_WIDTH_PX + (i * 2 * HALF_WIDTH_PX) / (count - 1));
}

const snapNotesX: NoteCommands['snapNotesX'] = (tx: Tx, { ids, count }) => {
  if (!(Number.isInteger(count) && count >= 2 && count <= 2 * HALF_WIDTH_PX + 1)) {
    tx.fail({ key: 'cmd.notes.gridCount' });
  }
  for (const n of existingNotes(tx, ids)) {
    const x = snapXToGrid(n.x, count);
    if (x !== n.x) tx.update('notes', n.id, { x });
  }
};

const quantizeNotes: NoteCommands['quantizeNotes'] = (tx: Tx, { ids, div }) => {
  if (!(Number.isInteger(div) && div >= 1 && div <= MAX_SNAP_DIV)) tx.fail({ key: 'cmd.notes.snapDiv' });
  let grown = 0;
  for (const n of existingNotes(tx, ids)) {
    const t = quantizeBeat(n.t, div, 'round', tx.rounding);
    let end = t;
    if (n.kind === 'hold') {
      if (Beats.eq(n.t, n.end)) {
        end = zeroHoldEnd(t, div);
        grown++;
      } else {
        end = quantizeBeat(n.end, div, 'round', tx.rounding);
        if (!Beats.lt(t, end)) end = Beats.step(t, div, 1);
      }
    }
    const patch: Patch<Note> = {};
    if (!Beats.eq(t, n.t)) patch.t = t;
    if (!Beats.eq(end, n.end)) patch.end = end;
    if (Object.keys(patch).length > 0) tx.update('notes', n.id, patch);
  }
  noteGrownNote(tx, grown, div);
};

const setHitsound: NoteCommands['setHitsound'] = (tx: Tx, { ids, hitsound }) => {
  checkHitsound(tx, hitsound);
  for (const n of existingNotes(tx, ids)) tx.update('notes', n.id, { hitsound });
};

const moveNotesToLine: NoteCommands['moveNotesToLine'] = (tx: Tx, { ids, line }) => {
  noteLine(tx, line);
  const notes = existingNotes(tx, ids);
  for (const n of notes) if (n.line !== line) tx.update('notes', n.id, { line });
  if (notes.length > 0) tx.setSelection({ activeLine: line, notes: notes.map((n) => n.id) });
};

export const noteCommands: NoteCommands = {
  addNote,
  addNotes,
  deleteNotes,
  patchNotes,
  exprNotes,
  moveNotes,
  setNoteTimes,
  convertNotes,
  mirrorNotesX,
  setSide,
  setFake,
  snapNotesX,
  quantizeNotes,
  setHitsound,
  moveNotesToLine,
};
